import { ProductVisibilityType } from "@omni/types/api/product/bff/shared/enums/productVisibilityType.enum";
import { RequireChildrenFor } from "@omni/types/components/utils/children.types";
import { isDefined } from "@omni/utils/helper.utils";
import { createContext, use, useState } from "react";

import { StorePickerType } from "@/components/Store/StorePicker/types/storePickerType.enum";
import { GetStoreStrategy } from "@/services/store.service";
import { StorePickerModalStates } from "@/utils/enums";
import { useStoreCode } from "@/utils/hooks/store/useStoreCode.hook";

import { useECommerceProduct } from "../hooks/useECommerceProduct.hook";

type StorePickerModalContextValue = {
    storePickerModalState: StorePickerModalStates;
    setStorePickerModalState: React.Dispatch<
        React.SetStateAction<StorePickerModalStates>
    >;
    storePickerType: StorePickerType;
    storeCode: string | null;
};

export type StorePickerModalContextProviderProps = {};

export const StorePickerModalContext = createContext<
    StorePickerModalContextValue | undefined
>(undefined);

StorePickerModalContext.displayName = "StorePickerModalContext";

export const StorePickerModalContextProvider = ({
    children
}: RequireChildrenFor<StorePickerModalContextProviderProps>) => {
    const [storePickerModalState, setStorePickerModalState] =
        useState<StorePickerModalStates>(StorePickerModalStates.Closed);

    const { visibilityType } = useECommerceProduct();

    const storeCode = useStoreCode({
        strategy: GetStoreStrategy.Session
    });

    const storePickerType =
        visibilityType === ProductVisibilityType.OnlyAvailableInStore ||
        visibilityType === ProductVisibilityType.Delivery
            ? StorePickerType.INFO
            : StorePickerType.SELECT;

    return (
        <StorePickerModalContext
            value={{
                storePickerModalState,
                setStorePickerModalState,
                storePickerType,
                storeCode: storeCode ?? null
            }}
        >
            {children}
        </StorePickerModalContext>
    );
};

export const useStorePickerModalContext = (): StorePickerModalContextValue => {
    const context = use<StorePickerModalContextValue | undefined>(
        StorePickerModalContext
    );
    if (!isDefined(context)) {
        throw new Error(`${StorePickerModalContext.displayName} is undefined.`);
    }
    return context;
};
